// Motor operativo "Machote Operativo Meta Patrimonio" — 100% frontend.
export const IVA = 0.16;

export const r2 = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

export function fmtMXN(n: number): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}
export function fmtPct(n: number, d = 2): string {
  return `${new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number.isFinite(n) ? n : 0)}%`;
}
export function fmtFecha(iso?: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}
/** dd/mm/aaaa a partir de la cadena ISO local (sin conversión de zona horaria). */
export function fmtFechaCorta(iso?: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "—";
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
}

/* ---------------------------------- Fechas --------------------------------- */
export const isoDe = (d: Date) => d.toISOString().slice(0, 10);
export const aDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y || 1970, (m || 1) - 1, d || 1));
};
export const diasEntre = (a: string, b: string) =>
  Math.round((aDate(b).getTime() - aDate(a).getTime()) / 86400000);
export const sumarDias = (iso: string, n: number) =>
  isoDe(new Date(aDate(iso).getTime() + n * 86400000));

/** Ajuste tipo WORKDAY: mueve al siguiente día hábil (evita sábado, domingo y festivos). */
export function siguienteHabil(iso: string, festivos: string[]): string {
  let cur = iso;
  for (let k = 0; k < 30; k++) {
    const dow = aDate(cur).getUTCDay();
    if (dow !== 0 && dow !== 6 && !festivos.includes(cur)) return cur;
    cur = sumarDias(cur, 1);
  }
  return cur;
}

export function fechaProgramada(
  desembolso: string,
  periodo: number,
  diaPago: number,
  festivos: string[],
): string {
  return siguienteHabil(fechaOriginalProgramada(desembolso, periodo, diaPago), festivos);
}

/** Fecha mensual teórica antes del ajuste por fin de semana o día inhábil. */
export function fechaOriginalProgramada(
  desembolso: string,
  periodo: number,
  diaPago: number,
): string {
  const base = aDate(desembolso);
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth() + periodo;
  const ultimo = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const dia = Math.min(Math.max(1, Math.floor(diaPago) || 1), ultimo);
  return isoDe(new Date(Date.UTC(y, m, dia)));
}

/* ------------------------- Parámetros variables del crédito ---------------- */
export type TipoCredito = "CRÉDITO SIMPLE" | "CRÉDITO REVOLVENTE";
export type PeriodicidadPago = "SEMANAL" | "CATORCENAL" | "QUINCENAL" | "MENSUAL";
export type Moneda = "MXN" | "UDIS";
export type TipoOperacion = "Empresarial" | "Personal" | "Consumo";
export type TipoTasa = "FIJA" | "VARIABLE";
export type Amortizacion =
  "Francés (Pagos Homologados)" | "Alemán (Capital Fijo)" | "Americano (Bullet)";
export type CalculoInteres = "Saldos insolutos" | "Saldo global";
export type BaseMora = "Saldo vencido no pagado (capital + interés)" | "Saldo insoluto";
/** Método de cálculo del monto financiado con cargos financiados. */
export type MetodoComision = "Suma directa" | "Circular (Meta Patrimonio)";
export const METODOS_COMISION: MetodoComision[] = ["Suma directa", "Circular (Meta Patrimonio)"];
/** Método de aplicación del dinero dentro de cada nivel de prelación. */
export type MetodoPrelacion = "Secuencial estricto" | "Proporcional base:IVA";
export const METODOS_PRELACION: MetodoPrelacion[] = [
  "Secuencial estricto",
  "Proporcional base:IVA",
];
/** Qué se ajusta cuando el acreditado hace un abono extra a capital. */
export type AplicacionPrepago =
  "Reducir cuota" | "Reducir plazo (requiere convenio por escrito con el acreditado)";
export const APLICACIONES_PREPAGO: AplicacionPrepago[] = [
  "Reducir cuota",
  "Reducir plazo (requiere convenio por escrito con el acreditado)",
];

export const TIPOS_CREDITO: TipoCredito[] = ["CRÉDITO SIMPLE", "CRÉDITO REVOLVENTE"];
export const PERIODICIDADES_PAGO: PeriodicidadPago[] = [
  "SEMANAL",
  "CATORCENAL",
  "QUINCENAL",
  "MENSUAL",
];
export const MONEDAS: Moneda[] = ["MXN", "UDIS"];
export const TIPOS_OPERACION: TipoOperacion[] = ["Empresarial", "Personal", "Consumo"];
export const TIPOS_TASA: TipoTasa[] = ["FIJA", "VARIABLE"];
export const AMORTIZACIONES: Amortizacion[] = [
  "Francés (Pagos Homologados)",
  "Alemán (Capital Fijo)",
  "Americano (Bullet)",
];
export const CALCULOS_INTERES: CalculoInteres[] = ["Saldos insolutos", "Saldo global"];
export const BASES_MORA: BaseMora[] = [
  "Saldo vencido no pagado (capital + interés)",
  "Saldo insoluto",
];

/** Número de pagos por año según periodicidad (base comercial 360 para catorcenal). */
export const PAGOS_POR_ANIO: Record<PeriodicidadPago, number> = {
  SEMANAL: 52,
  CATORCENAL: 360 / 14,
  QUINCENAL: 24,
  MENSUAL: 12,
};

export const DIAS_PERIODO: Record<PeriodicidadPago, number> = {
  SEMANAL: 7,
  CATORCENAL: 14,
  QUINCENAL: 15,
  MENSUAL: 30,
};

export const ETIQUETA_PERIODO: Record<PeriodicidadPago, string> = {
  SEMANAL: "semanal",
  CATORCENAL: "catorcenal",
  QUINCENAL: "quincenal",
  MENSUAL: "mensual",
};

export interface ParamsCredito {
  tipoCredito: TipoCredito;
  tipoPago: PeriodicidadPago;
  moneda: Moneda;
  valorUdi: number;
  tipoOperacion: TipoOperacion;
  tipoTasa: TipoTasa;
  tasaReferencia: number; // % anual (TIIE u otra referencia)
  spread: number; // puntos porcentuales
  amortizacion: Amortizacion;
  calculoInteres: CalculoInteres;
  tasaAnual: number; // % nominal anual (aplica si tipoTasa = FIJA)
  tasaMoratoria: number; // % anual
  numImpagos: number; // umbral de períodos impagos para cartera vencida
  periodosGracia: number; // períodos iniciales de solo interés
  diasGraciaMora?: number; // días de tolerancia antes de que corra la mora (0 = día siguiente)
  ivaEnCuota: boolean; // esquema Meta Patrimonio: cuota calculada con tasa x 1.16
  metodoComisionFinanciada?: MetodoComision; // cómo se financian las comisiones al monto
  metodoPrelacion?: MetodoPrelacion; // cómo se reparte el dinero dentro de cada nivel
  aplicacionPrepago?: AplicacionPrepago; // reducir cuota (re-amortizar) o reducir plazo
  baseMora: BaseMora; // base de cálculo del interés moratorio
  ivaInteresOrdinario: boolean; // ¿se calcula IVA al interés ordinario?
  ivaMoraActiva: boolean; // ¿causa IVA la mora (y la pena)?
  permitePagoAnticipado: boolean;
  penalidadPrepago: boolean;
  penalidadPrepagoPct: number; // % sobre capital prepagado
  requiereAval: boolean;
}

/** Defaults que vive en el catálogo de productos (la tasa la aporta el producto). */
export type ParamsProducto = Omit<ParamsCredito, "tasaAnual" | "tasaMoratoria">;

/** Valor de la UDI por default (parámetro global editable). */
export const VALOR_UDI_DEFAULT = 8.83083;

export const PARAMS_PRODUCTO_BASE: ParamsProducto = {
  tipoCredito: "CRÉDITO SIMPLE",
  tipoPago: "MENSUAL",
  moneda: "MXN",
  valorUdi: VALOR_UDI_DEFAULT,
  tipoOperacion: "Empresarial",
  tipoTasa: "FIJA",
  tasaReferencia: 11.25,
  spread: 20,
  amortizacion: "Francés (Pagos Homologados)",
  calculoInteres: "Saldos insolutos",
  numImpagos: 3,
  periodosGracia: 0,
  diasGraciaMora: 0,
  ivaEnCuota: true,
  metodoComisionFinanciada: "Suma directa",
  metodoPrelacion: "Secuencial estricto",
  aplicacionPrepago: "Reducir cuota",
  baseMora: "Saldo vencido no pagado (capital + interés)",
  ivaInteresOrdinario: true,
  ivaMoraActiva: true,
  permitePagoAnticipado: true,
  penalidadPrepago: false,
  penalidadPrepagoPct: 2,
  requiereAval: false,
};

/** Resuelve los parámetros de un crédito nuevo a partir de los defaults del producto. */
export function paramsDeProducto(prod: Producto): ParamsCredito {
  return {
    ...prod.params,
    tasaAnual: prod.tasaAnual,
    tasaMoratoria: r2(prod.tasaAnual * prod.factorMora),
    diasGraciaMora: prod.diasGracia,
  };
}

/** ¿El crédito genera IVA sobre el interés ordinario? (independiente de si va dentro de la cuota) */
export const causaIvaInteres = (p: ParamsCredito) => p.ivaInteresOrdinario ?? true;
/** ¿El IVA del interés viaja dentro de la tasa/cuota (esquema Meta Patrimonio)? */
export const ivaDentroDeCuota = (p: ParamsCredito) => Boolean(p.ivaEnCuota) && causaIvaInteres(p);
/** Método de comisión financiada vigente (default nuevo del cotizador: suma directa). */
export const metodoComisionDe = (p: ParamsCredito): MetodoComision =>
  p.metodoComisionFinanciada ?? "Suma directa";
/** Método de aplicación dentro de la prelación (default: secuencial estricto). */
export const metodoPrelacionDe = (p: ParamsCredito): MetodoPrelacion =>
  p.metodoPrelacion === "Proporcional base:IVA" ? "Proporcional base:IVA" : "Secuencial estricto";
/** Tratamiento de los abonos extra a capital (default: re-amortizar reduciendo la cuota). */
export const aplicacionPrepagoDe = (p: ParamsCredito): AplicacionPrepago =>
  p.aplicacionPrepago === "Reducir plazo (requiere convenio por escrito con el acreditado)"
    ? "Reducir plazo (requiere convenio por escrito con el acreditado)"
    : "Reducir cuota";
/** ¿La mora y la pena convencional causan IVA? */
export const causaIvaMora = (p: ParamsCredito) => p.ivaMoraActiva ?? true;
/** Fórmula visible en la UI. */
export const FORMULA_MORA =
  "Interés moratorio = (Capital vencido + Interés ordinario vencido) × Tasa moratoria anual / 360 × Días de atraso";
/** Días de gracia de mora vigentes (crédito > producto). */
export const diasGraciaMoraDe = (p: ParamsCredito, prod: Producto) =>
  Number.isFinite(p.diasGraciaMora as number)
    ? Math.max(0, Number(p.diasGraciaMora))
    : prod.diasGracia;

export const baseMoraDe = (p: ParamsCredito): BaseMora =>
  p.baseMora === "Saldo insoluto"
    ? "Saldo insoluto"
    : "Saldo vencido no pagado (capital + interés)";

export const tasaAnualEfectiva = (p: ParamsCredito) =>
  p.tipoTasa === "VARIABLE"
    ? (Number(p.tasaReferencia) || 0) + (Number(p.spread) || 0)
    : Number(p.tasaAnual) || 0;

/** Fecha del pago t según la periodicidad (con ajuste a día hábil). */
export function fechaPeriodo(
  desembolso: string,
  periodo: number,
  diaPago: number,
  tipoPago: PeriodicidadPago,
  festivos: string[],
): string {
  if (tipoPago === "MENSUAL") return fechaProgramada(desembolso, periodo, diaPago, festivos);
  return siguienteHabil(sumarDias(desembolso, DIAS_PERIODO[tipoPago] * periodo), festivos);
}

/** Fecha teórica del período antes del ajuste al siguiente día hábil. */
export function fechaOriginalPeriodo(
  desembolso: string,
  periodo: number,
  diaPago: number,
  tipoPago: PeriodicidadPago,
): string {
  if (tipoPago === "MENSUAL") return fechaOriginalProgramada(desembolso, periodo, diaPago);
  return sumarDias(desembolso, DIAS_PERIODO[tipoPago] * periodo);
}

/* --------------------------------- Catálogo -------------------------------- */
export interface Producto {
  id: string;
  nombre: string;
  montoMin: number;
  montoMax: number;
  /** Si se define, el piso se calcula como UDIS × valor UDI vigente. */
  montoMinUdis?: number | null;
  /** Si se define, el tope se calcula como UDIS × valor UDI vigente. */
  montoMaxUdis?: number | null;
  plazoMin: number;
  plazoMax: number;
  tasaAnual: number; // % nominal anual
  factorMora: number;
  comisionAperturaPct: number;
  diasGracia: number;
  requiereGarantia: boolean;
  aforoMin: number;
  antiguedadMaxAnios: number;
  penaConvencional: number;
  tipoGarantia: string;
  /** Producto de línea revolvente: se opera en la subsección "Línea Revolvente". */
  esRevolvente?: boolean;
  params: ParamsProducto;
}

/** Límites de monto vigentes: los montos en UDIS se recalculan con el valor UDI del día. */
export function limitesMonto(prod: Producto, valorUdi: number) {
  const udi = Number(valorUdi) || 0;
  const minUdis = prod.montoMinUdis ?? null;
  const maxUdis = prod.montoMaxUdis ?? null;
  return {
    min: minUdis ? r2(minUdis * udi) : prod.montoMin,
    max: maxUdis ? r2(maxUdis * udi) : prod.montoMax,
    minUdis,
    maxUdis,
  };
}

export const PRODUCTOS_INICIALES: Producto[] = [
  {
    id: "express",
    nombre: "Meta Express",
    montoMin: 1,
    montoMax: r2(3000 * VALOR_UDI_DEFAULT),
    montoMaxUdis: 3000,
    plazoMin: 12,
    plazoMax: 18,
    tasaAnual: 80,
    factorMora: 2,
    comisionAperturaPct: 3.5,
    diasGracia: 0,
    requiereGarantia: false,
    aforoMin: 0,
    antiguedadMaxAnios: 99,
    penaConvencional: 300,
    tipoGarantia: "No aplica",
    params: { ...PARAMS_PRODUCTO_BASE, tipoOperacion: "Consumo", numImpagos: 2 },
  },
  {
    id: "impulso",
    nombre: "Meta Impulso",
    montoMin: r2(3000 * VALOR_UDI_DEFAULT),
    montoMinUdis: 3000,
    montoMax: 300000,
    plazoMin: 12,
    plazoMax: 36,
    tasaAnual: 60,
    factorMora: 2,
    comisionAperturaPct: 3.5,
    diasGracia: 0,
    requiereGarantia: true,
    aforoMin: 2.5,
    antiguedadMaxAnios: 4,
    penaConvencional: 500,
    tipoGarantia: "Garantía mobiliaria",
    params: { ...PARAMS_PRODUCTO_BASE, tipoOperacion: "Personal" },
  },
  {
    id: "pyme",
    nombre: "Meta PYME",
    montoMin: 300001,
    montoMax: 5000000,
    plazoMin: 12,
    plazoMax: 60,
    tasaAnual: 40,
    factorMora: 2,
    comisionAperturaPct: 3.5,
    diasGracia: 0,
    requiereGarantia: true,
    aforoMin: 2.5,
    antiguedadMaxAnios: 99,
    penaConvencional: 700,
    tipoGarantia: "Garantía mobiliaria",
    params: { ...PARAMS_PRODUCTO_BASE, tipoOperacion: "Empresarial" },
  },
  {
    id: "revolvente",
    nombre: "Meta Revolvente",
    montoMin: 10000,
    montoMax: 500000,
    plazoMin: 1,
    plazoMax: 1,
    tasaAnual: 70,
    factorMora: 2,
    comisionAperturaPct: 0,
    diasGracia: 0,
    requiereGarantia: false,
    aforoMin: 0,
    antiguedadMaxAnios: 99,
    penaConvencional: 500,
    tipoGarantia: "No aplica",
    esRevolvente: true,
    params: {
      ...PARAMS_PRODUCTO_BASE,
      tipoCredito: "CRÉDITO REVOLVENTE",
      tipoOperacion: "Empresarial",
      ivaEnCuota: false,
      numImpagos: 3,
    },
  },
];

export const FESTIVOS_INICIALES = [
  "2026-01-01",
  "2026-02-02",
  "2026-03-16",
  "2026-04-02",
  "2026-04-03",
  "2026-05-01",
  "2026-09-16",
  "2026-11-16",
  "2026-12-25",
  "2027-01-01",
  "2027-02-01",
  "2027-03-15",
  "2027-05-01",
  "2027-09-16",
  "2027-11-15",
  "2027-12-25",
];

/* ------------------------ Comisiones y gastos (CONDUSEF) ------------------- */
export const CATEGORIAS_COMISION = [
  "Contratación o Apertura",
  "Pago tardío",
  "Administración",
  "Gastos de Cobranza",
  "Pena Convencional",
  "Anualidad",
  "Disposición en efectivo",
  "Reposición de contrato",
  "Consulta de saldo",
  "Prepago o pago anticipado",
];

export const CATEGORIAS_GASTO = [
  "Seguro de vida",
  "Seguro del bien",
  "Avalúo",
  "Gestoría",
  "Notaría",
  "Investigación crediticia",
  "Otro",
];

export type TipoCargo = "Comisión" | "Gasto";
export type BaseCalculo = "Monto financiado (circular)" | "Importe fijo";
export type TipoImporte = "%" | "$";
export type Periodicidad =
  "Única vez" | "A la fecha de firma" | "Por evento de mora" | "Mensual" | "Por período";
/** Cargos cobrados una sola vez al inicio (financiados o no). */
export const esCargoInicial = (c: Cargo) =>
  c.periodicidad === "Única vez" || c.periodicidad === "A la fecha de firma";
/** Cargos recurrentes que se suman al pago de cada período. */
export const esCargoPeriodo = (c: Cargo) =>
  c.periodicidad === "Mensual" || c.periodicidad === "Por período";
/** Un cargo desactivado (Aplicar = No) calcula 0. */
export const cargoActivo = (c: Cargo) => c.aplicar !== false;
/** Pena convencional: no entra a la tabla programada ni al CAT. */
export const esCargoPena = (c: Cargo) => c.periodicidad === "Por evento de mora";

export interface Cargo {
  id: string;
  productoId: string;
  tipo: TipoCargo;
  categoria: string;
  nombre: string;
  base: BaseCalculo;
  tipoImporte: TipoImporte;
  importe: number;
  periodicidad: Periodicidad;
  momento: string;
  financiado: boolean;
  causaIva: boolean;
  /** Aplicar = Sí / No. Si es false el cargo calcula 0. */
  aplicar?: boolean;
}

let seq = 0;
export const nuevoId = (p = "id") => `${p}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const CARGOS_INICIALES: Cargo[] = PRODUCTOS_INICIALES.flatMap((p) => [
  {
    id: `${p.id}-apertura`,
    productoId: p.id,
    tipo: "Comisión" as TipoCargo,
    categoria: "Contratación o Apertura",
    nombre: "Comisión por apertura",
    base: "Monto financiado (circular)" as BaseCalculo,
    tipoImporte: "%" as TipoImporte,
    importe: p.comisionAperturaPct,
    periodicidad: "Única vez" as Periodicidad,
    momento: "Al desembolso",
    financiado: true,
    causaIva: true,
    aplicar: true,
  },
  {
    id: `${p.id}-pena`,
    productoId: p.id,
    tipo: "Comisión" as TipoCargo,
    categoria: "Pena Convencional",
    nombre: "Pena convencional por atraso",
    base: "Importe fijo" as BaseCalculo,
    tipoImporte: "$" as TipoImporte,
    importe: p.penaConvencional,
    periodicidad: "Por evento de mora" as Periodicidad,
    momento: "Al incurrir en mora",
    financiado: false,
    causaIva: true,
    aplicar: true,
  },
]);

/* -------------------------------- Solicitud -------------------------------- */
export interface Garantia {
  descripcion: string;
  valor: number;
  fechaFactura: string;
  tipo: string;
}

export interface Solicitud {
  productoId: string;
  monto: number;
  plazo: number; // número de pagos
  fechaDesembolso: string;
  diaPago: number;
  garantia: Garantia;
  params: ParamsCredito;
  avalNombre: string;
}

export interface Pago {
  id: string;
  /**
   * Excepción manual: fuerza la aplicación a un período concreto.
   * Por regla el pago se aplica al período MÁS VENCIDO primero, así que
   * normalmente va indefinido.
   */
  periodo?: number;
  fecha: string;
  monto: number;
  referencia: string;
  /** Medio utilizado para el movimiento; alimenta la matriz de riesgo PLD. */
  instrumento: string;
}

/** Pena convencional capturada manualmente por el ejecutivo (ya no se genera sola). */
export interface PenaManual {
  id: string;
  periodo: number;
  fecha: string;
  importe: number;
  causaIva: boolean;
  referencia?: string;
}

/* --------------------------------- Cálculos -------------------------------- */
export function pmt(monto: number, i: number, n: number): number {
  if (n <= 0) return 0;
  if (i === 0) return monto / n;
  return (monto * i) / (1 - Math.pow(1 + i, -n));
}

/** Tasa i* por bisección tal que Σ flujo_t/(1+i)^t = valorPresente */
export function tir(flujos: number[], valorPresente: number): number {
  const vpn = (i: number) =>
    flujos.reduce((a, f, k) => a + f / Math.pow(1 + i, k + 1), 0) - valorPresente;
  if (valorPresente <= 0 || vpn(0) <= 0) return 0;
  let lo = 0;
  let hi = 10;
  let mid = 0;
  for (let k = 0; k < 400; k++) {
    mid = (lo + hi) / 2;
    const v = vpn(mid);
    if (Math.abs(v) < 1e-10) break;
    if (v > 0) lo = mid;
    else hi = mid;
  }
  return mid;
}

export interface Condiciones {
  params: ParamsCredito;
  tasaAnualAplicada: number; // %
  pagosPorAnio: number;
  etiquetaPeriodo: string;
  tasaPeriodica: number; // decimal
  tasaPeriodicaCuota: number; // decimal usada para calcular la cuota (x1.16 si IVA en cuota)
  tasaMensual: number; // alias legado = tasaPeriodica
  tasaMensualConIva: number;
  tasaMoraAnual: number; // decimal
  pctCircularConIva: number;
  gastosFijosFinanciados: number;
  metodoComision: MetodoComision;
  montoFinanciado: number;
  comisionesFinanciadas: number;
  montoNetoDesembolsado: number;
  cargosInicialesNoFinanciados: number;
  cargosPeriodo: number;
  desembolsoNeto: number;
  cobrosExternos: number;
  cobrosExternosSinIva: number;
  cuotaPeriodo: number;
  cuotaMensual: number; // alias legado = cuotaPeriodo
  catConIva: number;
  catSinIva: number;
  totalEstimado: number;
  /** Pena convencional DERIVADA de los cargos del crédito (sin IVA). */
  penaConvencional: number;
  /** Pena convencional derivada con IVA (fuente única de verdad del indicador). */
  penaConIva: number;
  /** Cuántos cargos "Por evento de mora" activos aportan a la pena. */
  penaCargos: number;
  periodosGracia: number;
  filasBase: FilaBase[];
  /** Importe resuelto de cada cargo del crédito (fuente única de verdad). */
  detalleCargos: DetalleCargo[];
}

export interface FilaBase {
  periodo: number;
  esGracia: boolean;
  saldoInicial: number;
  capital: number;
  interes: number;
  ivaInteres: number;
  cuotaBase: number;
  cargosPeriodo: number;
  pagoTotal: number;
  cuotaEsperada: number;
  saldoFinal: number;
}

/** Construye el calendario teórico (sin fechas) con periodicidad, sistema, gracia y base de interés. */
export function calendarioBase(
  monto: number,
  p: ParamsCredito,
  plazoPagos: number,
  tasaPeriodica: number,
  cargosPeriodo = 0,
): FilaBase[] {
  const n = Math.max(1, Math.floor(plazoPagos) || 1);
  const g = Math.min(Math.max(0, Math.floor(p.periodosGracia) || 0), n - 1);
  const nAm = Math.max(1, n - g);
  const i = tasaPeriodica;
  const conIvaInt = causaIvaInteres(p);
  const ivaDentro = ivaDentroDeCuota(p);
  const iCuota = ivaDentro ? i * (1 + IVA) : i;
  const global = p.calculoInteres === "Saldo global";
  const cuotaFr = pmt(monto, iCuota, nAm);
  const capitalFijo = monto / nAm;
  const cargos = r2(Math.max(0, cargosPeriodo));

  const out: FilaBase[] = [];
  let saldo = monto;
  for (let t = 1; t <= n; t++) {
    const saldoInicial = saldo;
    const interes = r2((global ? monto : saldoInicial) * i);
    const ivaInteres = conIvaInt ? r2(interes * IVA) : 0;
    const esGracia = t <= g;
    let capital: number;
    if (esGracia) capital = 0;
    else if (p.amortizacion === "Americano (Bullet)") capital = t === n ? saldoInicial : 0;
    else if (p.amortizacion === "Alemán (Capital Fijo)")
      capital = saldoInicial / Math.max(1, n - t + 1);
    else capital = global ? capitalFijo : cuotaFr - interes - (ivaDentro ? ivaInteres : 0);
    capital = r2(Math.min(Math.max(0, capital), saldoInicial));
    if (t === n) capital = r2(saldoInicial);
    const saldoFinal = r2(saldoInicial - capital);
    const cuotaBase = r2(capital + interes + (ivaDentro ? ivaInteres : 0));
    const pagoTotal = r2(cuotaBase + (ivaDentro ? 0 : ivaInteres) + cargos);
    out.push({
      periodo: t,
      esGracia,
      saldoInicial: r2(saldoInicial),
      capital,
      interes,
      ivaInteres,
      cuotaBase,
      cargosPeriodo: cargos,
      pagoTotal,
      cuotaEsperada: pagoTotal,
      saldoFinal: Math.abs(saldoFinal) < 0.005 ? 0 : saldoFinal,
    });
    saldo = saldoFinal;
  }
  return out;
}

/** Importe calculado de un cargo sobre una base (con IVA si lo causa). */
export function importeCargo(c: Cargo, base: number): number {
  if (!cargoActivo(c)) return 0;
  const bruto =
    c.tipoImporte === "%" ? (base * (Number(c.importe) || 0)) / 100 : Number(c.importe) || 0;
  return r2(bruto * (c.causaIva ? 1 + IVA : 1));
}

/**
 * Copia la plantilla de comisiones y gastos de un producto para usarla dentro de
 * una solicitud. Los cambios en la copia no afectan la plantilla del producto.
 */
export function copiarCargosDeProducto(plantilla: Cargo[], productoId: string): Cargo[] {
  return plantilla
    .filter((c) => c.productoId === productoId)
    .map((c) => ({ ...c, id: nuevoId("cargo") }));
}

/** Clasificación de un cargo según su impacto en el cálculo. */
export type ClaseCargo =
  "Financiado" | "Inicial no financiado" | "Por período" | "Pena convencional" | "Sin impacto";

export const claseCargo = (c: Cargo): ClaseCargo =>
  esCargoPena(c)
    ? "Pena convencional"
    : esCargoInicial(c)
      ? c.financiado
        ? "Financiado"
        : "Inicial no financiado"
      : esCargoPeriodo(c)
        ? "Por período"
        : "Sin impacto";

/** Importe resuelto de un cargo por el MISMO motor que calcula el monto financiado. */
export interface DetalleCargo {
  id: string;
  nombre: string;
  clase: ClaseCargo;
  /** Base sobre la que se calculó (monto financiado si es circular, monto solicitado si no). */
  base: number;
  /** Porcentaje configurado en esta solicitud (undefined si el cargo es importe fijo). */
  pct?: number | undefined;
  bruto: number;
  iva: number;
  total: number;
}

/** Totales por clasificación de cargos (con IVA cuando el cargo lo causa). */
export function resumenCargos(cargos: Cargo[], productoId: string, monto: number) {
  const propios = cargos.filter((c) => c.productoId === productoId);
  const suma = (fn: (c: Cargo) => boolean) =>
    r2(
      propios
        .filter((c) => cargoActivo(c) && fn(c))
        .reduce((a, c) => a + importeCargo(c, monto), 0),
    );
  return {
    total: propios.length,
    activos: propios.filter(cargoActivo).length,
    financiados: suma((c) => c.financiado && esCargoInicial(c)),
    inicialesNoFinanciados: suma((c) => !c.financiado && esCargoInicial(c)),
    porPeriodo: suma((c) => !c.financiado && esCargoPeriodo(c)),
    penas: suma(esCargoPena),
  };
}

/**
 * Totales de cargos tomados del motor de condiciones (fuente única de verdad):
 * los renglones, las tarjetas y el monto financiado cuadran centavo a centavo.
 */
export function resumenCargosDeCondiciones(cond: Condiciones, cargos: Cargo[], productoId: string) {
  const propios = cargos.filter((c) => c.productoId === productoId);
  const suma = (k: ClaseCargo) =>
    r2(cond.detalleCargos.filter((d) => d.clase === k).reduce((a, d) => a + d.total, 0));
  return {
    total: propios.length,
    activos: propios.filter(cargoActivo).length,
    financiados: suma("Financiado"),
    inicialesNoFinanciados: suma("Inicial no financiado"),
    porPeriodo: suma("Por período"),
    penas: suma("Pena convencional"),
  };
}

/** Importe resuelto de un cargo concreto, tal como lo usó el motor. */
export const detalleDeCargo = (cond: Condiciones, id: string): DetalleCargo | undefined =>
  cond.detalleCargos.find((d) => d.id === id);

export function calcularCondiciones(prod: Producto, cargos: Cargo[], sol: Solicitud): Condiciones {
  const p = sol.params;
  const propios = cargos.filter((c) => c.productoId === prod.id && cargoActivo(c));
  const pagosPorAnio = PAGOS_POR_ANIO[p.tipoPago];
  const tasaAnualAplicada = tasaAnualEfectiva(p);
  const tasaPeriodica = tasaAnualAplicada / 100 / pagosPorAnio;
  const tasaPeriodicaCuota = ivaDentroDeCuota(p) ? tasaPeriodica * (1 + IVA) : tasaPeriodica;
  const tasaMoraAnual = (Number(p.tasaMoratoria) || 0) / 100;

  const pctCircularConIva = propios
    .filter(
      (c) =>
        c.financiado &&
        c.base === "Monto financiado (circular)" &&
        c.tipoImporte === "%" &&
        esCargoInicial(c),
    )
    .reduce((a, c) => a + (c.importe / 100) * (c.causaIva ? 1 + IVA : 1), 0);

  const gastosFijosFinanciados = propios
    .filter(
      (c) =>
        c.financiado && esCargoInicial(c) && (c.tipoImporte === "$" || c.base === "Importe fijo"),
    )
    .reduce((a, c) => a + c.importe * (c.causaIva ? 1 + IVA : 1), 0);

  const monto = Number.isFinite(sol.monto) ? sol.monto : 0;
  const plazo = Math.max(1, Math.floor(sol.plazo) || 1);
  const metodoComision = metodoComisionDe(p);

  // ---- Fuente única de verdad de comisiones y gastos -----------------------
  // 1) Se resuelve el monto financiado (circular o suma directa).
  // 2) Cada cargo se DERIVA de ese monto: importe, IVA y total.
  // 3) Los totales por clasificación se suman de esos mismos importes, de modo
  //    que se cumple exacto: montoFinanciado = monto solicitado + financiados.
  const todos = cargos.filter((c) => c.productoId === prod.id);
  const esFinanciadoCircular = (c: Cargo) =>
    metodoComision === "Circular (Meta Patrimonio)" &&
    c.financiado &&
    esCargoInicial(c) &&
    !esCargoPena(c) &&
    c.tipoImporte === "%" &&
    c.base === "Monto financiado (circular)";

  const montoFinanciadoRef =
    metodoComision === "Circular (Meta Patrimonio)" && 1 - pctCircularConIva > 0
      ? (monto + gastosFijosFinanciados) / (1 - pctCircularConIva)
      : monto;

  const detalleCargos: DetalleCargo[] = todos.map((c) => {
    const activo = cargoActivo(c);
    const base = esFinanciadoCircular(c) ? r2(montoFinanciadoRef) : monto;
    const bruto = !activo
      ? 0
      : r2(
          c.tipoImporte === "%" ? (base * (Number(c.importe) || 0)) / 100 : Number(c.importe) || 0,
        );
    const iva = c.causaIva ? r2(bruto * IVA) : 0;
    return {
      id: c.id,
      nombre: c.nombre,
      clase: claseCargo(c),
      base,
      pct: c.tipoImporte === "%" ? Number(c.importe) || 0 : undefined,
      bruto,
      iva,
      total: r2(bruto + iva),
    };
  });
  const sumaClase = (k: ClaseCargo, campo: "total" | "bruto" = "total") =>
    r2(detalleCargos.filter((d) => d.clase === k).reduce((a, d) => a + d[campo], 0));

  const comisionesFinanciadas = sumaClase("Financiado");
  const montoFinanciado = r2(monto + comisionesFinanciadas);
  const montoNetoDesembolsado = r2(montoFinanciado - comisionesFinanciadas);

  // Cargos iniciales no financiados: se cobran por fuera y reducen el desembolso neto.
  const cobrosExternosSinIva = sumaClase("Inicial no financiado", "bruto");
  const cobrosExternos = sumaClase("Inicial no financiado");
  const cargosInicialesNoFinanciados = cobrosExternos;
  const desembolsoNeto = r2(montoNetoDesembolsado - cargosInicialesNoFinanciados);

  // Cargos por período no financiados (se suman al pago total de cada período).
  const cargosPeriodo = sumaClase("Por período");

  // Pena convencional DERIVADA: Σ de los cargos "Por evento de mora" con Aplicar = Sí
  // de ESTA solicitud (nunca de la plantilla del producto ni de una constante).
  const penaBruta = sumaClase("Pena convencional", "bruto");
  const penaTotal = sumaClase("Pena convencional");
  const penaNumCargos = detalleCargos.filter(
    (d) => d.clase === "Pena convencional" && d.bruto > 0,
  ).length;

  if (import.meta.env?.DEV) {
    const desfase = r2(montoFinanciado - (monto + comisionesFinanciadas));
    if (Math.abs(desfase) > TOL_CENTAVO) {
      console.warn(
        `[operativo] Invariante de monto financiado roto por ${desfase}: ` +
          `${montoFinanciado} ≠ ${monto} + ${comisionesFinanciadas}`,
      );
    }
  }

  const filasBase = calendarioBase(montoFinanciado, p, plazo, tasaPeriodica, cargosPeriodo);
  const gracia = Math.min(Math.max(0, Math.floor(p.periodosGracia) || 0), plazo - 1);
  const cuotaPeriodo = filasBase[Math.min(gracia, filasBase.length - 1)]?.cuotaEsperada ?? 0;

  const conIva = filasBase.map((f) => f.cuotaEsperada);
  const sinIva = filasBase.map((f) => r2(f.cuotaEsperada - f.ivaInteres));

  const catConIva =
    (Math.pow(1 + tir(conIva, r2(montoNetoDesembolsado - cobrosExternos)), pagosPorAnio) - 1) * 100;
  const catSinIva =
    (Math.pow(1 + tir(sinIva, r2(montoNetoDesembolsado - cobrosExternosSinIva)), pagosPorAnio) -
      1) *
    100;

  return {
    params: p,
    tasaAnualAplicada,
    pagosPorAnio,
    etiquetaPeriodo: ETIQUETA_PERIODO[p.tipoPago],
    tasaPeriodica,
    tasaPeriodicaCuota,
    tasaMensual: tasaPeriodica,
    tasaMensualConIva: tasaPeriodicaCuota,
    tasaMoraAnual,
    pctCircularConIva,
    gastosFijosFinanciados: r2(gastosFijosFinanciados),
    metodoComision,
    montoFinanciado,
    comisionesFinanciadas,
    montoNetoDesembolsado,
    cargosInicialesNoFinanciados,
    cargosPeriodo,
    desembolsoNeto,
    cobrosExternos,
    cobrosExternosSinIva,
    cuotaPeriodo,
    cuotaMensual: cuotaPeriodo,
    catConIva,
    catSinIva,
    totalEstimado: r2(conIva.reduce((a, b) => a + b, 0)),
    penaConvencional: penaBruta,
    penaConIva: penaTotal,
    penaCargos: penaNumCargos,
    periodosGracia: gracia,
    filasBase,
    detalleCargos,
  };
}

/* ----------------------------- Tabla programada ---------------------------- */
export interface FilaProgramada extends FilaBase {
  /** Fecha teórica antes de recorrer fines de semana o festivos. */
  fechaOriginal: string;
  /** Fecha exigible ya recorrida al siguiente día hábil. */
  fechaProg: string;
  /** Días corridos desde la fecha hábil anterior (o desembolso en el primer período). */
  diasPeriodo: number;
}

export function tablaProgramada(
  _prod: Producto,
  cond: Condiciones,
  sol: Solicitud,
  festivos: string[],
): FilaProgramada[] {
  let fechaAnterior = sol.fechaDesembolso;
  return cond.filasBase.map((f) => {
    const fechaOriginal = fechaOriginalPeriodo(
      sol.fechaDesembolso,
      f.periodo,
      sol.diaPago,
      cond.params.tipoPago,
    );
    const fechaProg = siguienteHabil(fechaOriginal, festivos);
    const diasPeriodo = diasEntre(fechaAnterior, fechaProg);
    fechaAnterior = fechaProg;
    return { ...f, fechaOriginal, fechaProg, diasPeriodo };
  });
}

/* -------------------------- Motor real de aplicación ----------------------- */
/** Tolerancia de redondeo: un residuo ≤ 1 centavo se considera liquidado. */
export const TOL_CENTAVO = 0.01;

/**
 * Segmento cronológico de devengo de mora. Cada pago parcial CRISTALIZA el segmento
 * abierto en su fecha; el remanente sigue devengando desde ahí hacia la fecha de consulta.
 */
export interface SegmentoMora {
  desde: string;
  hasta: string;
  dias: number;
  base: number;
  mora: number;
  ivaMora: number;
  /** "pago" = cristalizado por un pago en esa fecha; "abierto" = corre hasta la fecha de consulta. */
  motivo: "pago" | "abierto";
}

export interface AplicRenglon {
  concepto: string;
  adeudo: number;
  aplicado: number;
  restante: number;
}

/** Aplicación de un pago a UN período concreto (el propio o un vencido anterior). */
export interface AplicacionPeriodo {
  periodo: number;
  /** true = período al que se capturó el pago; false = período vencido anterior. */
  propio: boolean;
  dias: number;
  desde: string;
  mora: number;
  ivaMora: number;
  renglones: AplicRenglon[];
  total: number;
}

export interface TramoAplicado {
  fecha: string;
  monto: number;
  diasAtraso: number;
  /** Fecha desde la que se contaron los días de mora de este tramo. */
  desde: string;
  mora: number;
  ivaMora: number;
  pena: number;
  ivaPena: number;
  aplIvaPena: number;
  aplPena: number;
  aplIvaMora: number;
  aplMora: number;
  aplIvaInteres: number;
  aplInteres: number;
  aplCapital: number;
  /** Abono extra a capital (sólo cuando ya no hay períodos vencidos ni cuota exigible). */
  aplCapitalExtra: number;
  /** Cascada del pago: períodos vencidos más antiguos primero y al final el propio. */
  aplicaciones: AplicacionPeriodo[];
  sobrepago: number;
  referencia: string;
}

export interface FilaReal extends FilaProgramada {
  /** true si la cuota del período se recalculó por un abono extra a capital. */
  reamortizada?: boolean;
  tramos: TramoAplicado[];
  montoPagado: number;
  diasAtraso: number;
  /** Estado derivado: el período quedó cubierto (cuota + mora/pena devengadas). */
  liquidado: boolean;
  /** Adeudo residual del período a la fecha de consulta (cuota + mora/pena no cubiertas). */
  adeudoPeriodo: number;
  /** Estatus derivado del período, recalculado en cada render. */
  estatusPeriodo: "Cubierto" | "En mora" | "Pago parcial" | "Pendiente" | "Sin exigibilidad";
  /** Segmentos cronológicos de devengo de mora del período. */
  segmentos: SegmentoMora[];
  /** Mora del segmento abierto (devengada hasta la fecha de consulta, aún no liquidada). */
  moraVigente: number;
  ivaMoraVigente: number;
  /** Fecha en que quedó liquidado el período (congela el conteo de días). */
  fechaLiquidacion?: string;
  /** Fecha real del último pago registrado del período (ISO). */
  fechaUltimoPago?: string;
  mora: number;
  ivaMora: number;
  pena: number;
  ivaPena: number;
  aplIvaPena: number;
  aplPena: number;
  aplIvaMora: number;
  aplMora: number;
  aplIvaInteres: number;
  aplInteres: number;
  aplCapital: number;
  penalidadPrepago: number;
  saldoFinalReal: number;
  atraso: boolean;
  cerrado: boolean;
}

export interface Backlog {
  ivaPena: number;
  pena: number;
  ivaMora: number;
  mora: number;
  ivaInteres: number;
  interes: number;
}

/** Dónde aterrizó un pago, decidido por las reglas de prelación y antigüedad. */
export interface AplicacionPago {
  periodos: number[];
  capitalExtra: number;
  sobrepago: number;
}

export interface ResultadoReal {
  filas: FilaReal[];
  backlog: Backlog;
  sobrepago: number;
  /** Mora devengada y aún no liquidada de los segmentos abiertos, a la fecha de consulta. */
  moraVigente: number;
  ivaMoraVigente: number;
  /** Interés ordinario DEVENGADO del período vigente (por días corridos, no exigible). */
  interesVigente: number;
  ivaInteresVigente: number;
  /** Días transcurridos / totales del período vigente (auditoría del devengo). */
  diasPeriodoVigente: { transcurridos: number; totales: number };
  /** Fecha de consulta usada para el devengo. */
  hoy: string;
  penalidadPrepago: number;
  saldoCapital: number;
  /** Aplicación DERIVADA por pago (id del pago → períodos cubiertos y abono extra). */
  aplicacionPagos: Record<string, AplicacionPago>;
  pagado: {
    total: number;
    capital: number;
    interes: number;
    ivaInteres: number;
    mora: number;
    ivaMora: number;
    pena: number;
    ivaPena: number;
  };
}

const nuevoBacklog = (): Backlog => ({
  ivaPena: 0,
  pena: 0,
  ivaMora: 0,
  mora: 0,
  ivaInteres: 0,
  interes: 0,
});

/**
 * Aplica dinero al par (IVA, base) de un nivel de prelación.
 * - "Secuencial estricto": agota primero el IVA del nivel y luego su concepto base.
 * - "Proporcional base:IVA": reparto 1:0.16 (esquema original del cliente).
 */
function aplicarPar(
  disp: number,
  base: number,
  iva: number,
  metodo: MetodoPrelacion = "Proporcional base:IVA",
) {
  const total = r2(base + iva);
  if (disp <= 0 || total <= 0)
    return { pagoIva: 0, pagoBase: 0, restIva: iva, restBase: base, disp };
  if (disp >= total)
    return { pagoIva: iva, pagoBase: base, restIva: 0, restBase: 0, disp: r2(disp - total) };
  if (metodo === "Secuencial estricto") {
    const pagoIva = r2(Math.min(disp, Math.max(0, iva)));
    const resto = r2(disp - pagoIva);
    const pagoBase = r2(Math.min(resto, Math.max(0, base)));
    return {
      pagoIva,
      pagoBase,
      restIva: r2(iva - pagoIva),
      restBase: r2(base - pagoBase),
      disp: r2(resto - pagoBase),
    };
  }
  const pagoIva = r2((disp * iva) / total);
  const pagoBase = r2(disp - pagoIva);
  return { pagoIva, pagoBase, restIva: r2(iva - pagoIva), restBase: r2(base - pagoBase), disp: 0 };
}

function aplicarSimple(disp: number, monto: number) {
  if (disp <= 0 || monto <= 0) return { pago: 0, rest: monto, disp };
  const pago = Math.min(disp, monto);
  return { pago: r2(pago), rest: r2(monto - pago), disp: r2(disp - pago) };
}

export const r4 = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 10000) / 10000;

/** Interés moratorio: base × (tasa anual / 360) × días de atraso. */
export function moraCalculada(base: number, tasaMoraAnual: number, diasAtraso: number): number {
  if (base <= 0 || diasAtraso <= 0) return 0;
  return r4(base * (tasaMoraAnual / 360) * diasAtraso);
}

export interface AdeudoConceptos {
  pena: number;
  ivaPena: number;
  mora: number;
  ivaMora: number;
  interes: number;
  ivaInteres: number;
  capital: number;
}

export interface PasoAplicacion {
  orden: number;
  concepto: string;
  adeudo: number;
  aplicado: number;
  restante: number;
}

/** Reparto de un nivel (IVA + base) con precisión de 4 decimales, según el método. */
function parPreciso(
  disp: number,
  base: number,
  iva: number,
  metodo: MetodoPrelacion = "Proporcional base:IVA",
) {
  const total = r4(base + iva);
  if (disp <= 0 || total <= 0)
    return { pagoBase: 0, pagoIva: 0, restBase: base, restIva: iva, disp };
  if (disp >= total)
    return { pagoBase: base, pagoIva: iva, restBase: 0, restIva: 0, disp: r4(disp - total) };
  if (metodo === "Secuencial estricto") {
    const pagoIva = r4(Math.min(disp, Math.max(0, iva)));
    const resto = r4(disp - pagoIva);
    const pagoBase = r4(Math.min(resto, Math.max(0, base)));
    return {
      pagoBase,
      pagoIva,
      restBase: r4(base - pagoBase),
      restIva: r4(iva - pagoIva),
      disp: r4(resto - pagoBase),
    };
  }
  const pagoBase = r4(disp / (1 + (iva > 0 ? iva / base : 0)));
  const pagoIva = r4(disp - pagoBase);
  return { pagoBase, pagoIva, restBase: r4(base - pagoBase), restIva: r4(iva - pagoIva), disp: 0 };
}

/**
 * Aplica un pago por prelación (RN-06/07/08). En modo "Secuencial estricto" agota
 * cada nivel antes de pasar al siguiente (IVA pena → pena → IVA mora → mora →
 * IVA interés → interés → capital); en modo "Proporcional base:IVA" reparte 1:0.16
 * dentro de cada nivel, como en el Excel original del cliente.
 */
export function aplicarPrelacion(
  pago: number,
  adeudo: AdeudoConceptos,
  metodo: MetodoPrelacion = "Proporcional base:IVA",
) {
  let disp = r4(Math.max(0, pago));
  const pasos: PasoAplicacion[] = [];
  const restante: AdeudoConceptos = { ...adeudo };
  const secuencial = metodo === "Secuencial estricto";
  const push = (concepto: string, adeudoIni: number, aplicado: number, rest: number) => {
    if (adeudoIni <= 0 && aplicado <= 0) return;
    pasos.push({
      orden: pasos.length + 1,
      concepto,
      adeudo: r4(adeudoIni),
      aplicado: r4(aplicado),
      restante: r4(rest),
    });
  };
  /** En secuencial el IVA se muestra (y cobra) antes que su concepto base. */
  const nivel = (
    etiquetaBase: string,
    etiquetaIva: string,
    baseIni: number,
    ivaIni: number,
    res: { pagoBase: number; pagoIva: number; restBase: number; restIva: number },
  ) => {
    if (secuencial) {
      push(etiquetaIva, ivaIni, res.pagoIva, res.restIva);
      push(etiquetaBase, baseIni, res.pagoBase, res.restBase);
    } else {
      push(etiquetaBase, baseIni, res.pagoBase, res.restBase);
      push(etiquetaIva, ivaIni, res.pagoIva, res.restIva);
    }
  };

  const pena = parPreciso(disp, adeudo.pena, adeudo.ivaPena, metodo);
  restante.pena = pena.restBase;
  restante.ivaPena = pena.restIva;
  disp = pena.disp;
  nivel("Pena convencional", "IVA pena convencional", adeudo.pena, adeudo.ivaPena, pena);

  const mora = parPreciso(disp, adeudo.mora, adeudo.ivaMora, metodo);
  restante.mora = mora.restBase;
  restante.ivaMora = mora.restIva;
  disp = mora.disp;
  nivel("Interés moratorio", "IVA interés moratorio", adeudo.mora, adeudo.ivaMora, mora);

  const inte = parPreciso(disp, adeudo.interes, adeudo.ivaInteres, metodo);
  restante.interes = inte.restBase;
  restante.ivaInteres = inte.restIva;
  disp = inte.disp;
  nivel("Interés ordinario", "IVA interés ordinario", adeudo.interes, adeudo.ivaInteres, inte);

  const aplCap = Math.min(disp, Math.max(0, adeudo.capital));
  restante.capital = r4(adeudo.capital - aplCap);
  disp = r4(disp - aplCap);
  push("Capital", adeudo.capital, aplCap, restante.capital);

  return { pasos, restante, sobrepago: r4(Math.max(0, disp)) };
}

/* --------------------------- Motor real (cascada) -------------------------- */
/** Estado vivo de un período: sus conceptos pendientes y su cursor de devengo de mora. */
interface Bucket {
  fila: FilaReal;
  inicioMora: string;
  cursorMora: string;
  /** Fecha de inicio de devengo del interés ordinario del período. */
  inicioInteres: string;
  /** Cursor de devengo del interés ordinario (avanza con cada pago / corte). */
  cursorInteres: string;
  /** Saldo insoluto vivo sobre el que devenga el interés del período. */
  saldoInteres: number;
  /** Interés ordinario devengado por días efectivamente transcurridos. */
  interesDevengado: number;
  /** Interés ordinario ya exigido (cargado a pendInteres). */
  interesCargado: number;
  /** Interés ordinario efectivamente cobrado del período. */
  interesCobrado: number;
  /** Interés programado del período (exigible completo al llegar la fecha de corte). */
  interesProgramado: number;
  /** true si hubo pago anticipado: el interés final se arma por segmentos de días. */
  usaDevengo: boolean;
  pendPena: number;
  pendIvaPena: number;
  pendMora: number;
  pendIvaMora: number;
  pendInteres: number;
  pendIvaInteres: number;
  /** Capital de la cuota del mes aún no abonado. */
  pendCapital: number;
  /** Cargos por período (no financiados) aún no cobrados. */
  pendCargos: number;
  cuotaEsperada: number;
  cubiertoCuota: number;
  saldoInicial: number;
  capitalAbonado: number;
  capitalProgramado: number;
  penas: PenaManual[];
  cargadas: Set<string>;
  fechaCubierto?: string;
}

export function motorReal(
  prod: Producto,
  cond: Condiciones,
  sol: Solicitud,
  programada: FilaProgramada[],
  pagos: Pago[],
  penas: PenaManual[] = [],
  hoy: string = new Date().toISOString().slice(0, 10),
): ResultadoReal {
  // Corte temporal: sólo se consideran movimientos con fecha <= fecha de consulta.
  const pagosVigentes = pagos.filter((p) => p.fecha <= hoy);
  const penasVigentes = penas.filter((x) => x.fecha <= hoy);
  const backlog = nuevoBacklog();

  let sobrepago = 0;
  let penalidadPrepagoTotal = 0;
  let moraVigenteTotal = 0;
  let ivaMoraVigenteTotal = 0;
  let capitalTotal = 0;
  /** Resultado DERIVADO de la aplicación por pago (para mostrar "Se aplicó a"). */
  const aplicacionPagos: Record<string, AplicacionPago> = {};
  const pagado = {
    total: 0,
    capital: 0,
    interes: 0,
    ivaInteres: 0,
    mora: 0,
    ivaMora: 0,
    pena: 0,
    ivaPena: 0,
  };
  const filas: FilaReal[] = [];
  const buckets: Bucket[] = [];
  const diasGracia = diasGraciaMoraDe(cond.params, prod);
  const metodoPrel = metodoPrelacionDe(cond.params);
  const reamortizar = aplicacionPrepagoDe(cond.params) === "Reducir cuota";
  /**
   * Re-amortización como EVENTO: al ocurrir un abono extra a capital se calcula UNA vez
   * la cuota homologada de los períodos restantes y se CONGELA para todos ellos.
   */
  let cuotaCongelada: number | null = null;
  let capitalFijoCongelado = 0;
  /** Tasa diaria comercial (base 360) para el devengo del interés ordinario. */
  const tasaDia = cond.tasaAnualAplicada / 100 / 360;
  /** Interés ordinario devengado del período vigente (aún no exigible). */
  let interesVigente = 0;
  let ivaInteresVigente = 0;
  let diasVigenteTranscurridos = 0;
  let diasVigenteTotales = 0;

  const saldoDisponible = () => r2(Math.max(0, cond.montoFinanciado - capitalTotal));
  const pendienteCuota = (b: Bucket) =>
    r2(Math.max(0, b.pendCapital + b.pendInteres + b.pendIvaInteres));
  const cargosPend = (b: Bucket) =>
    r2(b.pendMora + b.pendIvaMora + b.pendPena + b.pendIvaPena + b.pendCargos);
  const bucketLiquidado = (b: Bucket) =>
    pendienteCuota(b) <= TOL_CENTAVO && cargosPend(b) <= TOL_CENTAVO;
  const baseMora = (b: Bucket) =>
    baseMoraDe(cond.params) === "Saldo insoluto"
      ? r2(Math.max(0, b.saldoInicial - b.capitalAbonado))
      : r2(Math.max(0, b.pendCapital + b.pendInteres));

  /**
   * Devengo del interés ordinario por días efectivamente transcurridos (base 360).
   * El interés no puede exigirse por adelantado: a la fecha `fecha` sólo es exigible
   * el interés corrido desde el inicio del período (o desde el último pago) hasta ahí.
   * Al llegar la fecha de corte el interés final es la suma de los segmentos de días
   * (saldo anterior antes del prepago + saldo reducido después), espejo de la mora.
   */
  const devengarInteres = (b: Bucket, fecha: string) => {
    const corte = b.fila.fechaProg;
    const anticipado = fecha < corte;
    const hasta = anticipado ? fecha : corte;
    const dias = Math.max(0, diasEntre(b.cursorInteres, hasta));
    if (dias > 0) {
      const base =
        cond.params.calculoInteres === "Saldo global"
          ? cond.montoFinanciado
          : Math.max(0, b.saldoInteres);
      b.interesDevengado = r2(b.interesDevengado + base * tasaDia * dias);
      b.cursorInteres = hasta;
    }
    if (anticipado) b.usaDevengo = true;
    // Interés exigible: devengado si el pago es anticipado o hubo prepago en el período;
    // el programado del período una vez llegada la fecha de corte sin pagos anticipados.
    const objetivo = anticipado || b.usaDevengo ? b.interesDevengado : b.interesProgramado;
    const nuevo = r2(Math.max(0, objetivo - b.interesCargado));
    if (nuevo > 0) {
      b.interesCargado = r2(b.interesCargado + nuevo);
      b.pendInteres = r2(b.pendInteres + nuevo);
      if (causaIvaInteres(cond.params)) b.pendIvaInteres = r2(b.pendIvaInteres + nuevo * IVA);
    }
  };

  /** Cierra el segmento abierto de mora en la fecha del pago y carga las penas ya devengadas. */
  const cristalizar = (b: Bucket, fecha: string) => {
    const desde = b.cursorMora;
    const dias = Math.max(0, diasEntre(desde, fecha));
    const base = baseMora(b);
    let mora = 0;
    let ivaMora = 0;
    if (dias > 0 && base > TOL_CENTAVO) {
      mora = r2(moraCalculada(base, cond.tasaMoraAnual, dias));
      ivaMora = causaIvaMora(cond.params) ? r2(mora * IVA) : 0;
      b.fila.segmentos.push({ desde, hasta: fecha, dias, base, mora, ivaMora, motivo: "pago" });
      b.pendMora = r2(b.pendMora + mora);
      b.pendIvaMora = r2(b.pendIvaMora + ivaMora);
      b.fila.mora = r2(b.fila.mora + mora);
      b.fila.ivaMora = r2(b.fila.ivaMora + ivaMora);
    }
    if (dias > 0) b.cursorMora = fecha;
    let pena = 0;
    let ivaPena = 0;
    for (const pn of b.penas) {
      if (b.cargadas.has(pn.id) || pn.fecha > fecha) continue;
      b.cargadas.add(pn.id);
      pena = r2(pena + pn.importe);
      if (pn.causaIva) ivaPena = r2(ivaPena + pn.importe * IVA);
    }
    if (pena > 0 || ivaPena > 0) {
      b.pendPena = r2(b.pendPena + pena);
      b.pendIvaPena = r2(b.pendIvaPena + ivaPena);
      b.fila.pena = r2(b.fila.pena + pena);
      b.fila.ivaPena = r2(b.fila.ivaPena + ivaPena);
    }
    return { desde, dias, mora, ivaMora, pena, ivaPena };
  };

  /** Aplica dinero a un período por prelación (IVA antes de su concepto base). */
  const aplicarABucket = (b: Bucket, dispIn: number, fecha: string, propio: boolean) => {
    // El interés exigible se devenga por días antes de aplicar la prelación.
    devengarInteres(b, fecha);
    const cris = cristalizar(b, fecha);
    let disp = r2(Math.max(0, dispIn));
    const ini = {
      ivaPena: b.pendIvaPena,
      pena: b.pendPena,
      ivaMora: b.pendIvaMora,
      mora: b.pendMora,
      ivaInteres: b.pendIvaInteres,
      interes: b.pendInteres,
      capital: b.pendCapital,
      cargos: b.pendCargos,
    };

    let a = aplicarPar(disp, b.pendPena, b.pendIvaPena, metodoPrel);
    b.pendPena = a.restBase;
    b.pendIvaPena = a.restIva;
    disp = a.disp;
    const aplPena = a.pagoBase;
    const aplIvaPena = a.pagoIva;

    a = aplicarPar(disp, b.pendMora, b.pendIvaMora, metodoPrel);
    b.pendMora = a.restBase;
    b.pendIvaMora = a.restIva;
    disp = a.disp;
    const aplMora = a.pagoBase;
    const aplIvaMora = a.pagoIva;

    a = aplicarPar(disp, b.pendInteres, b.pendIvaInteres, metodoPrel);
    b.pendInteres = a.restBase;
    b.pendIvaInteres = a.restIva;
    disp = a.disp;
    const aplInteres = a.pagoBase;
    const aplIvaInteres = a.pagoIva;

    const s = aplicarSimple(disp, b.pendCapital);
    b.pendCapital = s.rest;
    disp = s.disp;
    const aplCapital = s.pago;

    const sc = aplicarSimple(disp, b.pendCargos);
    b.pendCargos = sc.rest;
    disp = sc.disp;
    const aplCargos = sc.pago;

    const f = b.fila;
    f.aplPena = r2(f.aplPena + aplPena);
    f.aplIvaPena = r2(f.aplIvaPena + aplIvaPena);
    f.aplMora = r2(f.aplMora + aplMora);
    f.aplIvaMora = r2(f.aplIvaMora + aplIvaMora);
    f.aplInteres = r2(f.aplInteres + aplInteres);
    f.aplIvaInteres = r2(f.aplIvaInteres + aplIvaInteres);
    f.aplCapital = r2(f.aplCapital + aplCapital);
    b.cubiertoCuota = r2(b.cubiertoCuota + aplCapital + aplInteres + aplIvaInteres);
    b.capitalAbonado = r2(b.capitalAbonado + aplCapital);
    b.interesCobrado = r2(b.interesCobrado + aplInteres);
    // El capital abonado reduce la base de devengo de los días siguientes del período.
    b.saldoInteres = r2(Math.max(0, b.saldoInteres - aplCapital));
    capitalTotal = r2(capitalTotal + aplCapital);

    pagado.capital = r2(pagado.capital + aplCapital);
    pagado.interes = r2(pagado.interes + aplInteres);
    pagado.ivaInteres = r2(pagado.ivaInteres + aplIvaInteres);
    pagado.mora = r2(pagado.mora + aplMora);
    pagado.ivaMora = r2(pagado.ivaMora + aplIvaMora);
    pagado.pena = r2(pagado.pena + aplPena);
    pagado.ivaPena = r2(pagado.ivaPena + aplIvaPena);

    // El estatus de cubierto se RECOMPUTA: una pena o mora posterior reabre el período.
    if (bucketLiquidado(b)) b.fechaCubierto = b.fechaCubierto ?? fecha;
    else delete b.fechaCubierto;

    const renglon = (concepto: string, adeudo: number, aplicado: number, restante: number) => ({
      concepto,
      adeudo: r2(adeudo),
      aplicado: r2(aplicado),
      restante: r2(Math.max(0, restante)),
    });
    const renglones = [
      renglon("IVA pena convencional", ini.ivaPena, aplIvaPena, b.pendIvaPena),
      renglon("Pena convencional", ini.pena, aplPena, b.pendPena),
      renglon("IVA interés moratorio", ini.ivaMora, aplIvaMora, b.pendIvaMora),
      renglon("Interés moratorio", ini.mora, aplMora, b.pendMora),
      renglon("IVA interés ordinario", ini.ivaInteres, aplIvaInteres, b.pendIvaInteres),
      renglon("Interés ordinario", ini.interes, aplInteres, b.pendInteres),
      renglon("Capital (cuota del mes)", ini.capital, aplCapital, b.pendCapital),
      renglon("Cargos del período", ini.cargos, aplCargos, b.pendCargos),
    ].filter((r) => r.adeudo > 0 || r.aplicado > 0);

    const total = r2(
      aplIvaPena +
        aplPena +
        aplIvaMora +
        aplMora +
        aplIvaInteres +
        aplInteres +
        aplCapital +
        aplCargos,
    );
    const aplic: AplicacionPeriodo = {
      periodo: f.periodo,
      propio,
      dias: cris.dias,
      desde: cris.desde,
      mora: cris.mora,
      ivaMora: cris.ivaMora,
      renglones,
      total,
    };
    return {
      disp,
      aplic,
      cris,
      aplPena,
      aplIvaPena,
      aplMora,
      aplIvaMora,
      aplInteres,
      aplIvaInteres,
      aplCapital,
    };
  };

  /**
   * Cálculo del renglón de un período con el estado vivo del motor.
   * Se usa en la FASE A (construcción de toda la tabla) y al recomputar los
   * períodos restantes tras un evento de re-amortización.
   */
  const calcularPeriodo = (k: number) => {
    const prog = programada[k]!;
    // Saldo PROYECTADO: saldo real menos el capital programado que los períodos
    // anteriores aún deben (se pagará cuando esos períodos se paguen). Sin esto,
    // los períodos futuros abrirían todos con el mismo saldo y el último absorbería
    // todo el capital (cuota globo).
    const capitalPendienteAnterior = r2(
      buckets.slice(0, k).reduce((a, b) => a + Math.max(0, b.pendCapital), 0),
    );
    const saldoProyectado = r2(Math.max(0, saldoDisponible() - capitalPendienteAnterior));
    const cerrado = saldoProyectado <= 0.004;
    const saldoInicialReal = cerrado ? 0 : saldoProyectado;
    const baseInteres =
      cond.params.calculoInteres === "Saldo global" ? cond.montoFinanciado : saldoInicialReal;
    const interes = cerrado ? 0 : r2(baseInteres * cond.tasaPeriodica);
    const ivaInteres = cerrado || !causaIvaInteres(cond.params) ? 0 : r2(interes * IVA);
    const esUltimo = prog.periodo === programada.length;

    // ---- Re-amortización tras abonos extra a capital ("Reducir cuota").
    // La cuota homologada se calculó UNA vez en el evento del prepago y está congelada:
    // aquí sólo se amortiza normalmente contra ella (interés del saldo, capital = cuota − interés).
    let capRef = prog.capital;
    let cuotaRef = prog.cuotaEsperada;
    let reamortizada = false;
    if (reamortizar && cuotaCongelada !== null && !cerrado && !prog.esGracia) {
      const ivaDentro = ivaDentroDeCuota(cond.params);
      if (cond.params.amortizacion === "Americano (Bullet)") {
        capRef = esUltimo ? saldoInicialReal : 0;
      } else if (cond.params.amortizacion === "Alemán (Capital Fijo)") {
        capRef = capitalFijoCongelado;
      } else {
        capRef = r2(cuotaCongelada - interes - (ivaDentro ? ivaInteres : 0));
      }
      capRef = r2(Math.min(Math.max(0, capRef), saldoInicialReal));
      cuotaRef = r2(capRef + interes + ivaInteres + prog.cargosPeriodo);
      reamortizada = true;
    }

    const cuotaEsperada = cerrado
      ? 0
      : esUltimo
        ? r2(saldoInicialReal + interes + ivaInteres + prog.cargosPeriodo)
        : Math.min(cuotaRef, r2(saldoInicialReal + interes + ivaInteres + prog.cargosPeriodo));
    const capitalCuota = cerrado
      ? 0
      : esUltimo
        ? saldoInicialReal
        : r2(
            Math.max(
              0,
              Math.min(capRef, r2(cuotaEsperada - interes - ivaInteres - prog.cargosPeriodo)),
            ),
          );

    return {
      prog,
      cerrado,
      saldoInicialReal,
      interes,
      ivaInteres,
      cuotaEsperada,
      capitalCuota,
      reamortizada,
    };
  };

  // ---- FASE A: se construye TODA la tabla antes de aplicar un solo peso.
  for (let k = 0; k < programada.length; k++) {
    const c = calcularPeriodo(k);
    const prog = c.prog;

    const fila: FilaReal = {
      periodo: prog.periodo,
      esGracia: prog.esGracia,
      fechaOriginal: prog.fechaOriginal,
      fechaProg: prog.fechaProg,
      diasPeriodo: prog.diasPeriodo,
      saldoInicial: c.saldoInicialReal,
      capital: c.cerrado ? 0 : c.capitalCuota,
      reamortizada: c.reamortizada,
      interes: c.interes,
      ivaInteres: c.ivaInteres,
      cuotaBase: prog.cuotaBase,
      cargosPeriodo: prog.cargosPeriodo,
      pagoTotal: c.cuotaEsperada,
      cuotaEsperada: c.cuotaEsperada,
      saldoFinal: c.cerrado ? 0 : r2(c.saldoInicialReal - c.capitalCuota),
      tramos: [],
      montoPagado: 0,
      diasAtraso: 0,
      liquidado: false,
      adeudoPeriodo: 0,
      estatusPeriodo: "Pendiente",
      segmentos: [],
      moraVigente: 0,
      ivaMoraVigente: 0,
      mora: 0,
      ivaMora: 0,
      pena: 0,
      ivaPena: 0,
      aplIvaPena: 0,
      aplPena: 0,
      aplIvaMora: 0,
      aplMora: 0,
      aplIvaInteres: 0,
      aplInteres: 0,
      aplCapital: 0,
      penalidadPrepago: 0,
      saldoFinalReal: c.saldoInicialReal,
      atraso: false,
      cerrado: c.cerrado,
    };

    const inicioPeriodo =
      prog.periodo === 1
        ? sol.fechaDesembolso
        : (programada[prog.periodo - 2]?.fechaProg ?? sol.fechaDesembolso);

    const bucket: Bucket = {
      fila,
      inicioMora: sumarDias(prog.fechaProg, diasGracia),
      cursorMora: sumarDias(prog.fechaProg, diasGracia),
      inicioInteres: inicioPeriodo,
      cursorInteres: inicioPeriodo,
      saldoInteres: c.saldoInicialReal,
      interesDevengado: 0,
      interesCargado: 0,
      interesCobrado: 0,
      interesProgramado: c.interes,
      usaDevengo: false,
      pendPena: 0,
      pendIvaPena: 0,
      pendMora: 0,
      pendIvaMora: 0,
      // El interés ordinario NO se exige por adelantado: se carga al devengarse.
      pendInteres: 0,
      pendIvaInteres: 0,
      pendCapital: c.cerrado ? 0 : c.capitalCuota,
      pendCargos: c.cerrado ? 0 : r2(prog.cargosPeriodo),
      cuotaEsperada: c.cuotaEsperada,
      cubiertoCuota: 0,
      saldoInicial: c.saldoInicialReal,
      capitalAbonado: 0,
      capitalProgramado: r2(Math.min(prog.capital, c.saldoInicialReal)),
      penas: penasVigentes
        .filter((x) => x.periodo === prog.periodo && Number.isFinite(x.importe) && x.importe > 0)
        .sort((a, b) => a.fecha.localeCompare(b.fecha)),
      cargadas: new Set<string>(),
    };
    buckets.push(bucket);
    filas.push(fila);
  }

  /** Recomputa los períodos posteriores a un evento de prepago con la cuota congelada. */
  const recomputarDesde = (desde: number) => {
    for (let k = desde; k < buckets.length; k++) {
      const b = buckets[k]!;
      // Sólo se recomputan períodos intactos (sin devengo ni aplicación previa).
      if (b.capitalAbonado > TOL_CENTAVO || b.interesCargado > TOL_CENTAVO) continue;
      const c = calcularPeriodo(k);
      const f = b.fila;
      f.saldoInicial = c.saldoInicialReal;
      f.capital = c.cerrado ? 0 : c.capitalCuota;
      f.reamortizada = c.reamortizada;
      f.interes = c.interes;
      f.ivaInteres = c.ivaInteres;
      f.pagoTotal = c.cuotaEsperada;
      f.cuotaEsperada = c.cuotaEsperada;
      f.saldoFinal = c.cerrado ? 0 : r2(c.saldoInicialReal - c.capitalCuota);
      f.cerrado = c.cerrado;
      f.saldoFinalReal = c.saldoInicialReal;
      b.saldoInteres = c.saldoInicialReal;
      b.saldoInicial = c.saldoInicialReal;
      b.interesProgramado = c.interes;
      b.pendCapital = c.cerrado ? 0 : c.capitalCuota;
      b.pendCargos = c.cerrado ? 0 : r2(c.prog.cargosPeriodo);
      b.cuotaEsperada = c.cuotaEsperada;
      b.capitalProgramado = r2(Math.min(c.prog.capital, c.saldoInicialReal));
    }
  };

  /** Adeudo exigible vivo de un período (cuota + cargos/mora/pena pendientes). */
  const adeudoVivo = (b: Bucket) => r2(pendienteCuota(b) + cargosPend(b));

  /**
   * Adeudo vivo PROYECTADO a una fecha: incluye la mora y las penas manuales
   * devengadas hasta esa fecha que aún no se han cargado al período. Es la única
   * condición válida para saltar un período en la cascada: el flag `liquidado`
   * es histórico y una pena registrada después reabre el adeudo (RN-08).
   */
  const adeudoVivoA = (b: Bucket, fecha: string) => {
    const dias = Math.max(0, diasEntre(b.cursorMora, fecha));
    const base = baseMora(b);
    const moraProy =
      dias > 0 && base > TOL_CENTAVO
        ? r2(
            moraCalculada(base, cond.tasaMoraAnual, dias) *
              (causaIvaMora(cond.params) ? 1 + IVA : 1),
          )
        : 0;
    let penaProy = 0;
    for (const pn of b.penas) {
      if (b.cargadas.has(pn.id) || pn.fecha > fecha) continue;
      penaProy = r2(penaProy + pn.importe * (pn.causaIva ? 1 + IVA : 1));
    }
    return r2(adeudoVivo(b) + moraProy + penaProy);
  };

  // ---- FASE B: los pagos se aplican por REGLA, en orden cronológico,
  // al período MÁS VENCIDO primero; el remanente cascada hacia adelante.
  const pagosOrden = pagosVigentes
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => Number.isFinite(p.monto) && p.monto > 0)
    .sort((a, b) => a.p.fecha.localeCompare(b.p.fecha) || a.i - b.i);

  for (const { p: pago } of pagosOrden) {
    let disp = r2(pago.monto);
    const t: TramoAplicado = {
      fecha: pago.fecha,
      monto: r2(pago.monto),
      diasAtraso: 0,
      desde: pago.fecha,
      mora: 0,
      ivaMora: 0,
      pena: 0,
      ivaPena: 0,
      aplIvaPena: 0,
      aplPena: 0,
      aplIvaMora: 0,
      aplMora: 0,
      aplIvaInteres: 0,
      aplInteres: 0,
      aplCapital: 0,
      aplCapitalExtra: 0,
      aplicaciones: [],
      sobrepago: 0,
      referencia: pago.referencia,
    };

    let anclaIdx: number | null = null;
    let ultimoIdx: number | null = null;
    const aplicar = (k: number, propio: boolean) => {
      const b = buckets[k]!;
      const r = aplicarABucket(b, disp, pago.fecha, propio);
      disp = r.disp;
      if (r.aplic.renglones.length === 0 && r.aplic.total <= TOL_CENTAVO) return;
      t.aplicaciones.push(r.aplic);
      if (anclaIdx === null) {
        anclaIdx = k;
        t.diasAtraso = r.cris.dias;
        t.desde = r.cris.desde;
      }
      ultimoIdx = k;
      t.mora = r2(t.mora + r.cris.mora);
      t.ivaMora = r2(t.ivaMora + r.cris.ivaMora);
      t.pena = r2(t.pena + r.cris.pena);
      t.ivaPena = r2(t.ivaPena + r.cris.ivaPena);
      t.aplPena = r2(t.aplPena + r.aplPena);
      t.aplIvaPena = r2(t.aplIvaPena + r.aplIvaPena);
      t.aplMora = r2(t.aplMora + r.aplMora);
      t.aplIvaMora = r2(t.aplIvaMora + r.aplIvaMora);
      t.aplInteres = r2(t.aplInteres + r.aplInteres);
      t.aplIvaInteres = r2(t.aplIvaInteres + r.aplIvaInteres);
      t.aplCapital = r2(t.aplCapital + r.aplCapital);
    };

    // Excepción manual: el pago se fuerza a un período concreto.
    const manual =
      typeof pago.periodo === "number" && Number.isFinite(pago.periodo)
        ? buckets.findIndex((b) => b.fila.periodo === pago.periodo)
        : -1;
    if (manual >= 0) aplicar(manual, true);

    // 1) Períodos VENCIDOS a la fecha del pago, del más antiguo al más reciente.
    for (let k = 0; k < buckets.length && disp > TOL_CENTAVO; k++) {
      const b = buckets[k]!;
      if (b.fila.cerrado) continue;
      if (diasEntre(b.fila.fechaProg, pago.fecha) < 0) break;
      devengarInteres(b, pago.fecha);
      // Sólo se salta si NADA debe a la fecha del pago (nunca por el flag histórico).
      if (adeudoVivoA(b, pago.fecha) <= TOL_CENTAVO) continue;
      aplicar(k, k === manual);
    }

    // 2) Período VIGENTE (el primero aún no exigible): pago anticipado por devengo de días.
    let idxVigente = -1;
    if (disp > TOL_CENTAVO) {
      idxVigente = buckets.findIndex(
        (b) =>
          !b.fila.cerrado &&
          diasEntre(b.fila.fechaProg, pago.fecha) < 0 &&
          adeudoVivoA(b, pago.fecha) > TOL_CENTAVO,
      );

      if (idxVigente >= 0) aplicar(idxVigente, true);
    }

    const idxBase = ultimoIdx ?? (idxVigente >= 0 ? idxVigente : buckets.length - 1);
    const bucketBase = buckets[idxBase]!;
    const esUltimoBase = idxBase === buckets.length - 1;

    // 3) Sólo si ya no queda NADA exigible: abono extra a capital.
    if (disp > TOL_CENTAVO) {
      if (import.meta.env?.DEV) {
        const exigiblePendiente = buckets.some(
          (b) =>
            !b.fila.cerrado &&
            diasEntre(b.fila.fechaProg, pago.fecha) >= 0 &&
            adeudoVivo(b) > TOL_CENTAVO,
        );
        if (exigiblePendiente) {
          console.error(
            "[operativo] Invariante roto: abono extra a capital con períodos vencidos pendientes.",
          );
        }
      }
      const extra = aplicarSimple(disp, saldoDisponible());
      if (extra.pago > 0) {
        t.aplCapitalExtra = extra.pago;
        t.aplCapital = r2(t.aplCapital + extra.pago);
        bucketBase.fila.aplCapital = r2(bucketBase.fila.aplCapital + extra.pago);
        bucketBase.capitalAbonado = r2(bucketBase.capitalAbonado + extra.pago);
        bucketBase.saldoInteres = r2(Math.max(0, bucketBase.saldoInteres - extra.pago));
        capitalTotal = r2(capitalTotal + extra.pago);
        pagado.capital = r2(pagado.capital + extra.pago);
        // EVENTO de re-amortización: se congela una sola cuota homologada para
        // los períodos restantes sobre el saldo posterior al prepago.
        if (reamortizar) {
          const restantes = Math.max(1, buckets.length - (idxBase + 1));
          const nuevoSaldo = saldoDisponible();
          const ivaDentro = ivaDentroDeCuota(cond.params);
          const iCuota = ivaDentro ? cond.tasaPeriodica * (1 + IVA) : cond.tasaPeriodica;
          cuotaCongelada = nuevoSaldo > TOL_CENTAVO ? r2(pmt(nuevoSaldo, iCuota, restantes)) : 0;
          capitalFijoCongelado = r2(nuevoSaldo / restantes);
        }
        recomputarDesde(idxBase + 1);
      }
      disp = extra.disp;
    }

    // Penalidad por pago anticipado sobre el capital prepagado.
    if (cond.params.penalidadPrepago && cond.params.penalidadPrepagoPct > 0 && !esUltimoBase) {
      const exceso = r2(Math.max(0, bucketBase.capitalAbonado - bucketBase.capitalProgramado));
      const penalTotal = r2(((exceso * cond.params.penalidadPrepagoPct) / 100) * (1 + IVA));
      const nuevaPenal = r2(Math.max(0, penalTotal - bucketBase.fila.penalidadPrepago));
      if (nuevaPenal > 0) {
        bucketBase.fila.penalidadPrepago = r2(bucketBase.fila.penalidadPrepago + nuevaPenal);
        penalidadPrepagoTotal = r2(penalidadPrepagoTotal + nuevaPenal);
        const base = r2(nuevaPenal / (1 + IVA));
        bucketBase.pendPena = r2(bucketBase.pendPena + base);
        bucketBase.pendIvaPena = r2(bucketBase.pendIvaPena + r2(nuevaPenal - base));
        bucketBase.fila.pena = r2(bucketBase.fila.pena + base);
        bucketBase.fila.ivaPena = r2(bucketBase.fila.ivaPena + r2(nuevaPenal - base));
      }
    }

    if (disp > 0) {
      t.sobrepago = disp;
      sobrepago = r2(sobrepago + disp);
    }

    const anclaFila = buckets[anclaIdx ?? idxBase]!.fila;
    anclaFila.tramos.push(t);
    anclaFila.montoPagado = r2(anclaFila.montoPagado + t.monto);
    anclaFila.fechaUltimoPago = pago.fecha;
    pagado.total = r2(pagado.total + t.monto);
    aplicacionPagos[pago.id] = {
      periodos: t.aplicaciones.map((a) => a.periodo),
      capitalExtra: t.aplCapitalExtra,
      sobrepago: t.sobrepago,
    };
  }

  // ---- Cierre: devengo abierto hasta la fecha de consulta y estado derivado.
  let saldoAcum = cond.montoFinanciado;
  let vigenteTomado = false;
  for (const b of buckets) {
    const fila = b.fila;
    // El interés ordinario sólo se vuelve exigible al llegar la fecha de corte.
    const vencido = !fila.cerrado && diasEntre(fila.fechaProg, hoy) >= 0;
    if (vencido) devengarInteres(b, hoy);
    else if (!fila.cerrado && !vigenteTomado && saldoDisponible() > TOL_CENTAVO) {
      // Período VIGENTE: interés devengado por días corridos, aún no exigible.
      vigenteTomado = true;
      const dias = Math.max(0, diasEntre(b.cursorInteres, hoy));
      const base =
        cond.params.calculoInteres === "Saldo global"
          ? cond.montoFinanciado
          : Math.max(0, b.saldoInteres);
      const devengadoTotal = r2(b.interesDevengado + base * tasaDia * dias);
      interesVigente = r2(Math.max(0, devengadoTotal - b.interesCobrado));
      ivaInteresVigente = causaIvaInteres(cond.params) ? r2(interesVigente * IVA) : 0;
      diasVigenteTotales = Math.max(1, diasEntre(b.inicioInteres, fila.fechaProg));
      diasVigenteTranscurridos = Math.min(
        diasVigenteTotales,
        Math.max(0, diasEntre(b.inicioInteres, hoy)),
      );
    }
    const liquidado = bucketLiquidado(b);
    const fechaLiq = b.fechaCubierto ?? fila.fechaUltimoPago;
    if (liquidado && fechaLiq) fila.fechaLiquidacion = fechaLiq;

    const baseAbierta = baseMora(b);
    const diasAbiertos = liquidado ? 0 : Math.max(0, diasEntre(b.cursorMora, hoy));
    if (diasAbiertos > 0 && baseAbierta > TOL_CENTAVO) {
      const moraAb = r2(moraCalculada(baseAbierta, cond.tasaMoraAnual, diasAbiertos));
      const ivaMoraAb = causaIvaMora(cond.params) ? r2(moraAb * IVA) : 0;
      fila.segmentos.push({
        desde: b.cursorMora,
        hasta: hoy,
        dias: diasAbiertos,
        base: baseAbierta,
        mora: moraAb,
        ivaMora: ivaMoraAb,
        motivo: "abierto",
      });
      fila.mora = r2(fila.mora + moraAb);
      fila.ivaMora = r2(fila.ivaMora + ivaMoraAb);
      fila.moraVigente = moraAb;
      fila.ivaMoraVigente = ivaMoraAb;
      b.pendMora = r2(b.pendMora + moraAb);
      b.pendIvaMora = r2(b.pendIvaMora + ivaMoraAb);
      moraVigenteTotal = r2(moraVigenteTotal + moraAb);
      ivaMoraVigenteTotal = r2(ivaMoraVigenteTotal + ivaMoraAb);
    }

    // Penas capturadas que nunca se alcanzaron a cobrar.
    for (const pn of b.penas) {
      if (b.cargadas.has(pn.id)) continue;
      b.cargadas.add(pn.id);
      fila.pena = r2(fila.pena + pn.importe);
      b.pendPena = r2(b.pendPena + pn.importe);
      if (pn.causaIva) {
        fila.ivaPena = r2(fila.ivaPena + pn.importe * IVA);
        b.pendIvaPena = r2(b.pendIvaPena + pn.importe * IVA);
      }
    }

    // Residuales del período al backlog global (lo que sigue por cobrar).
    // El interés de períodos AÚN NO VENCIDOS nunca entra al backlog: no es exigible.
    backlog.pena = r2(backlog.pena + b.pendPena);
    backlog.ivaPena = r2(backlog.ivaPena + b.pendIvaPena);
    backlog.mora = r2(backlog.mora + b.pendMora);
    backlog.ivaMora = r2(backlog.ivaMora + b.pendIvaMora);
    if (vencido) {
      backlog.interes = r2(backlog.interes + b.pendInteres);
      backlog.ivaInteres = r2(backlog.ivaInteres + b.pendIvaInteres);
    }

    fila.diasAtraso = liquidado
      ? fila.fechaLiquidacion
        ? Math.max(0, diasEntre(b.inicioMora, fila.fechaLiquidacion))
        : 0
      : Math.max(0, diasEntre(b.inicioMora, hoy));
    fila.liquidado = liquidado;
    fila.adeudoPeriodo = liquidado ? 0 : r2(pendienteCuota(b) + cargosPend(b));
    fila.atraso = !liquidado && fila.diasAtraso > 0;
    // Cuota cubierta pero con pena/mora/cargos pendientes: NO es "Cubierto".
    const soloCargosPend = pendienteCuota(b) <= TOL_CENTAVO && cargosPend(b) > TOL_CENTAVO;
    fila.estatusPeriodo = fila.cerrado
      ? "Sin exigibilidad"
      : liquidado
        ? "Cubierto"
        : soloCargosPend
          ? "Pago parcial"
          : fila.atraso
            ? "En mora"
            : fila.montoPagado > 0 || fila.aplCapital > 0
              ? "Pago parcial"
              : "Pendiente";

    saldoAcum = r2(Math.max(0, saldoAcum - b.capitalAbonado));
    fila.saldoFinalReal = saldoAcum;
  }

  return {
    filas,
    backlog,
    sobrepago,
    moraVigente: moraVigenteTotal,
    ivaMoraVigente: ivaMoraVigenteTotal,
    interesVigente,
    ivaInteresVigente,
    diasPeriodoVigente: { transcurridos: diasVigenteTranscurridos, totales: diasVigenteTotales },
    hoy,
    penalidadPrepago: penalidadPrepagoTotal,
    saldoCapital: saldoDisponible(),
    aplicacionPagos,
    pagado,
  };
}

/* ------------------------------ Estado de cuenta --------------------------- */
export type Estatus = "LIQUIDADO" | "CARTERA VENCIDA" | "VENCIDO" | "SALDO PENDIENTE" | "VIGENTE";

export interface EstadoCuenta {
  saldoCapital: number;
  adeudoMora: number;
  adeudoInteres: number;
  capitalVencido: number;
  sobrepago: number;
  totalAlCorriente: number;
  totalLiquidarHoy: number;
  /** Interés ordinario devengado del período vigente (aún no exigible), sin IVA. */
  interesVigente: number;
  ivaInteresVigente: number;
  /** Interés del período vigente con IVA (base de la tarjeta y del pago total). */
  interesVigenteTotal: number;
  /** Días transcurridos / totales del período vigente (auditoría). */
  diasPeriodoVigente: { transcurridos: number; totales: number };
  composicionAlCorriente: { label: string; monto: number }[];
  composicionLiquidar: { label: string; monto: number }[];
  diasAtraso: number;
  numPagos: number;
  estatus: Estatus;
  moraAlDia: number;
  ivaMoraAlDia: number;
  periodosImpagos: number;
  umbralImpagos: number;
  carteraVencida: boolean;
  /** Fecha real del último pago registrado (ISO) o undefined si no hay pagos. */
  fechaUltimoPago?: string | undefined;
}

export function estadoDeCuenta(
  prod: Producto,
  cond: Condiciones,
  real: ResultadoReal,
  pagos: Pago[],
  hoy: string,
): EstadoCuenta {
  const b = real.backlog;
  const adeudoMora = r2(b.mora + b.ivaMora);
  const adeudoInteres = r2(b.interes + b.ivaInteres);
  const pena = r2(b.pena + b.ivaPena);

  // Capital vencido: capital de períodos ya exigibles no abonado
  let capitalVencido = 0;
  let diasAtraso = 0;
  let fechaUltimoPago: string | undefined;
  const pagosVigentes = pagos.filter((p) => p.fecha <= hoy);
  for (const p of pagosVigentes) {
    if (!fechaUltimoPago || p.fecha > fechaUltimoPago) fechaUltimoPago = p.fecha;
  }
  for (const f of real.filas) {
    if (f.cerrado) continue;
    // Estado derivado: un período liquidado no aporta atraso ni cuenta para impagos.
    if (f.liquidado) continue;
    const inicioMora = sumarDias(f.fechaProg, diasGraciaMoraDe(cond.params, prod));
    if (diasEntre(f.fechaProg, hoy) >= 0) {
      const capExigible = r2(Math.min(f.capital, f.saldoInicial));
      capitalVencido = r2(capitalVencido + Math.max(0, r2(capExigible - f.aplCapital)));
      if (diasEntre(inicioMora, hoy) > 0 && f.adeudoPeriodo > TOL_CENTAVO) {
        diasAtraso = Math.max(diasAtraso, diasEntre(inicioMora, hoy));
      }
    }
  }

  // Períodos vencidos sin liquidar: exactamente las filas marcadas "En mora"
  // en la tabla de control de pagos a la misma fecha de consulta.
  const periodosImpagos = real.filas.filter(
    (f) => !f.cerrado && !f.liquidado && f.atraso && f.adeudoPeriodo > TOL_CENTAVO,
  ).length;

  capitalVencido = r2(Math.min(capitalVencido, real.saldoCapital));

  // La mora devengada a la fecha de consulta ya viene del motor (segmentos abiertos)
  // e ingresó al backlog; se reporta aquí sin volver a sumarla a los totales.
  const moraAlDia = real.moraVigente;
  const ivaMoraAlDia = real.ivaMoraVigente;

  const totalAlCorriente = r2(
    Math.max(0, adeudoMora + adeudoInteres + pena + capitalVencido - real.sobrepago),
  );

  // ---- Interés devengado del período vigente (aún no exigible) ----
  // Fuente única: el MISMO devengo por días del motor de aplicación de pagos.
  const interesVigente = real.interesVigente;
  const ivaInteresVigente = real.ivaInteresVigente;
  const diasTranscurridos = real.diasPeriodoVigente.transcurridos;
  const diasTotales = real.diasPeriodoVigente.totales;
  const interesVigenteTotal = r2(interesVigente + ivaInteresVigente);

  const totalLiquidarHoy = r2(
    Math.max(
      0,
      real.saldoCapital + adeudoMora + adeudoInteres + pena + interesVigenteTotal - real.sobrepago,
    ),
  );

  const composicionAlCorriente = [
    { label: "Capital vencido", monto: capitalVencido },
    { label: "Intereses vencidos (c/IVA)", monto: adeudoInteres },
    { label: "Moratorios vencidos (c/IVA)", monto: adeudoMora },
    { label: "Penas pendientes (c/IVA)", monto: pena },
    ...(real.sobrepago > 0
      ? [{ label: "Saldo a favor aplicado", monto: r2(-real.sobrepago) }]
      : []),
  ];
  const composicionLiquidar = [
    { label: "Saldo de capital", monto: real.saldoCapital },
    { label: "Intereses vencidos (c/IVA)", monto: adeudoInteres },
    { label: "Interés del período vigente (c/IVA)", monto: interesVigenteTotal },
    { label: "Moratorios (c/IVA)", monto: adeudoMora },
    { label: "Penas pendientes (c/IVA)", monto: pena },
    ...(real.sobrepago > 0
      ? [{ label: "Saldo a favor aplicado", monto: r2(-real.sobrepago) }]
      : []),
  ];

  const umbralImpagos = Math.max(1, Math.floor(cond.params.numImpagos) || 1);
  const carteraVencida = periodosImpagos >= umbralImpagos;
  const pendiente = r2(adeudoMora + adeudoInteres + pena);
  let estatus: Estatus = "VIGENTE";
  if (real.saldoCapital <= TOL_CENTAVO && pendiente <= TOL_CENTAVO) estatus = "LIQUIDADO";
  else if (carteraVencida) estatus = "CARTERA VENCIDA";
  else if (diasAtraso > 0) estatus = "VENCIDO";
  else if (pendiente > TOL_CENTAVO) estatus = "SALDO PENDIENTE";

  // Invariante de clasificación: un crédito al corriente no puede tener adeudo vencido.
  if (diasAtraso === 0 && periodosImpagos === 0 && totalAlCorriente > TOL_CENTAVO) {
    console.error(
      `[operativo] Invariante roto: 0 días de atraso y 0 períodos vencidos, ` +
        `pero Total para ponerse al corriente = ${totalAlCorriente}`,
    );
  }

  return {
    saldoCapital: real.saldoCapital,
    adeudoMora,
    adeudoInteres,
    capitalVencido,
    sobrepago: real.sobrepago,
    totalAlCorriente,
    totalLiquidarHoy,
    interesVigente,
    ivaInteresVigente,
    interesVigenteTotal,
    diasPeriodoVigente: { transcurridos: diasTranscurridos, totales: diasTotales },
    composicionAlCorriente,
    composicionLiquidar,
    diasAtraso,
    numPagos: pagosVigentes.length,
    estatus,
    moraAlDia,
    ivaMoraAlDia,
    periodosImpagos,
    umbralImpagos,
    carteraVencida,
    fechaUltimoPago,
  };
}

/* ------------------------------ Escenario demo ----------------------------- */
export const SOLICITUD_DEMO: Solicitud = {
  productoId: "pyme",
  monto: 350000,
  plazo: 12,
  fechaDesembolso: "2026-01-15",
  diaPago: 15,
  params: {
    ...paramsDeProducto(PRODUCTOS_INICIALES.find((p) => p.id === "pyme")!),
    metodoComisionFinanciada: "Circular (Meta Patrimonio)",
  },
  avalNombre: "Grupo Industrial Torres S.A. de C.V.",
  garantia: {
    descripcion: "Maquinaria industrial CNC modelo 2024",
    valor: 900000,
    fechaFactura: "2024-03-10",
    tipo: "Prenda sobre maquinaria",
  },
};

/** Genera los pagos del escenario demo: cumplido, con un período en dos tramos con mora y liquidación final. */
export function pagosDemo(
  prod: Producto,
  cargos: Cargo[],
  sol: Solicitud,
  festivos: string[],
): Pago[] {
  const cond = calcularCondiciones(prod, cargos, sol);
  const prog = tablaProgramada(prod, cond, sol, festivos);
  const cuota = 38571.35;
  const base: Pago[] = [];
  prog.slice(0, prog.length - 1).forEach((f, idx) => {
    const per = idx + 1;
    if (per === 3) {
      base.push({
        id: `demo-3a`,
        fecha: sumarDias(f.fechaProg, 12),
        monto: 20000,
        referencia: "TRANSF-3A",
        instrumento: "Transferencia",
      });
      base.push({
        id: `demo-3b`,
        fecha: sumarDias(f.fechaProg, 23),
        monto: 18571.35,
        referencia: "TRANSF-3B",
        instrumento: "Transferencia",
      });
    } else {
      base.push({
        id: `demo-${per}`,
        fecha: f.fechaProg,
        monto: cuota,
        referencia: `TRANSF-${per}`,
        instrumento: "Transferencia",
      });
    }
  });
  const ultimo = prog[prog.length - 1]!;
  const sonda: Pago[] = [
    ...base,
    {
      id: "demo-final",
      fecha: ultimo.fechaProg,
      monto: 10_000_000,
      referencia: "LIQUIDACIÓN",
      instrumento: "Transferencia",
    },
  ];
  const real = motorReal(prod, cond, sol, prog, sonda, [], ultimo.fechaProg);
  const montoFinal = r2(10_000_000 - real.sobrepago);
  return [
    ...base,
    {
      id: "demo-final",
      fecha: ultimo.fechaProg,
      monto: montoFinal,
      referencia: "LIQUIDACIÓN",
      instrumento: "Transferencia",
    },
  ];
}
